import React, { Fragment, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ScrollableContent from '@wix/da-ds/pkg/ScrollableContent';
import UserAvatar from '@wix/da-shared-react/pkg/User/Avatar';
import SelectField from '@wix/da-ds/pkg/Dropdown/select/SelectField';
import ListItemCheckbox from '@wix/da-ds/pkg/List/ListItem/dsPresets/ListItemCheckbox';
import { GroupSelectValue } from '../../fields/GroupSelectField/types';
import FieldFormPopup from '../FieldFormPopup';
import {
  FieldFormPopupProps,
  FieldFormPopupState,
} from '../FieldFormPopup/types';
import {
  deselectGroup,
  selectGroupItemByValue,
  selectGroupsFirstItem,
} from '../../utils/select';

import s from './GroupSelectPopup.scss';

const GroupSelectPopup: React.FC<
  FieldFormPopupProps & FieldFormPopupState<GroupSelectValue>
> = ({ currentValue: groups, setValue: setGroups, ...props }) => {
  const { t } = useTranslation();

  const updateGroup = useCallback(
    group => {
      setGroups(
        groups.map(item => (item.groupId === group.groupId ? group : item))
      );
    },
    [setGroups, groups]
  );

  const handleCheckboxChange = useCallback(
    item => (isChecked: boolean) => {
      updateGroup(
        isChecked ? selectGroupsFirstItem(item) : deselectGroup(item)
      );
    },
    [updateGroup]
  );

  const handleFolderSelect = useCallback(
    item => value => {
      updateGroup(selectGroupItemByValue(item, value));
    },
    [updateGroup]
  );

  return (
    <FieldFormPopup
      {...props}
      heading={t('deviationSubmit.form.groups.modal.heading')}
      contentClassName={s['modal-content']}
    >
      <ScrollableContent>
        {groups.map(group => (
          <Fragment key={`group-${group.groupId}`}>
            <ListItemCheckbox
              biggerPaddings
              controlAlignment="center"
              key={group.groupId}
              className={s['group-item']}
              checked={group.selected}
              onChange={handleCheckboxChange(group)}
              label={
                <div className={s['group-label']}>
                  <UserAvatar size={32} user={group.group} withLink={false} />
                  {group.groupLabel}
                </div>
              }
            />
            {group.selected && (
              <SelectField
                id={`group-${group.groupId}-folders`}
                className={s['folder-select']}
                items={group.items}
                value={group.items.find(item => item.selected)?.value}
                onSelect={handleFolderSelect(group)}
                label={t(
                  'deviationSubmit.form.groups.modal.folderSelect.label'
                )}
                isLabelHidden
                withBlankOption={false}
              />
            )}
          </Fragment>
        ))}
      </ScrollableContent>
    </FieldFormPopup>
  );
};
GroupSelectPopup.displayName = 'GroupSelectPopup';

export default GroupSelectPopup;
